import { LinkedTasks } from '~src/linkedTasks/resources/LinkedTasks';

import { Formatters, Messages } from '../../codegen/Localize';
import { ActionHelpers, standardActionErrorMsg, standardActionSuccessMsg } from '../../commonInfra/contentActions/ActionHelpers';
import { IErrorItem } from '../../commonInfra/contentActions/MultipleItemResult';
import { openReactConfirmationDialog } from '../../commonInfra/dialogs/ReactConfirmationDialog';
import { TabDialogWidth } from '../../commonInfra/dialogs/style/TabDialogWidth';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { SchedulingErrorCode } from '../../ts/helpers/ErrorCodes';
import { L10n } from '../../ts/L10n/L10n';
import { FlowContentType } from '../../ts/models/ContentTypes';
import { INamedItemInfo } from '../../ts/models/resourceInfoParts';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { Set } from '../../ts/models/Set';
import { ContentUrlForItem } from '../../ts/services/ContentUrl';
import { RunFlowTasks } from '../resources/RunFlowTasks';

export function showResumeRunFlowTasksActionDialog(runFlowTaskInfos: Set<RunFlowTaskInfo>): Promise<any> {
  function resumeFlowOnOutputSteps(runFlowTaskInfos: Set<RunFlowTaskInfo>) {
    return RunFlowTasks.resume(runFlowTaskInfos.keys());
  }
  function resumeLinkedTasks(linkedTaskInfos: Set<RunFlowTaskInfo>) {
    return LinkedTasks.resumeLinkedTasks(linkedTaskInfos.pluck('linkedTaskId'));
  }

  const taskCount = runFlowTaskInfos.size();
  const flowTasks = runFlowTaskInfos.filter(item => (item as RunFlowTaskInfo).taskType === 'FlowTask');
  const linkedTasks = runFlowTaskInfos.filter(item => (item as RunFlowTaskInfo).taskType === 'LinkedTask');
  let dialogBody;
  if (taskCount > 0) {
    if (linkedTasks.size() === 0) {
      dialogBody =  Messages.runFlowTasksConfirmResume({
        TASK_COUNT: flowTasks.size(),
        TASK_COUNT_LOC: Formatters.formatCount(flowTasks.size()),
      });
    } else if (flowTasks.size() === 0) {
      dialogBody = Messages.linkedTaskResumeConfirmation({
        TASK_COUNT: linkedTasks.size(),
        TASK_COUNT_LOC: Formatters.formatCount(linkedTasks.size()),
      });
    } else {
      dialogBody = Messages.linkedTaskFlowTaskResumeConfirmation({
        TASK_COUNT: taskCount.toString(),
      });
    }
  }

  const resumeAction = () => {
    if (flowTasks.size() > 0 && linkedTasks.size() > 0) {
      return resumeFlowOnOutputSteps(flowTasks).then(() => {
        return resumeLinkedTasks(linkedTasks);
      });
    } else if (flowTasks.size() > 0) {
      return resumeFlowOnOutputSteps(flowTasks);
    } else if (linkedTasks.size() > 0) {
      return resumeLinkedTasks(linkedTasks);
    }
  };

  const dialogInstance = openReactConfirmationDialog<Set<RunFlowTaskInfo>, IResult>({
    title: Messages.runFlowTasksResume(),
    message: dialogBody,
    paramsForAction: runFlowTaskInfos,
    actionName: Messages.runFlowTasksResume(),
    action: resumeAction,
    dialogWidth: TabDialogWidth.width375px,
  });

  dialogInstance.result.then(result => ActionHelpers.displayStandardToastsForResult(
    runFlowTaskInfos,
    result,
    {
      success: standardActionSuccessMsg(Messages.runFlowTasksResumeSuccess),
      error: standardActionErrorMsg(Messages.runFlowTasksResumeError),
    },
    (errorItem: IErrorItem<RunFlowTaskInfo>) => {
      const namedItem: INamedItemInfo = errorItem.item.flow;
      return {
        itemName: namedItem.name,
        itemUrl: ContentUrlForItem(errorItem.item.flow, FlowContentType),
        errorMessage: errorMessageFor(errorItem)
      };
    }
  ));

  return Promise.resolve(dialogInstance.result);
}

function errorMessageFor(errorItem: IErrorItem<RunFlowTaskInfo>) {
  switch (errorItem.error.code) {
    case SchedulingErrorCode.SchedulingTaskNotFound:
      return Messages.errorRunFlowTaskNotFound({
        FLOW_NAME: L10n.elideIfLong(errorItem.item.flow.name),
        SCHEDULE_NAME: L10n.elideIfLong(errorItem.item.schedule.name)
      });
    default:
      return Messages.errorUnknown();
  }
}
